import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  AlertTriangle,
  BadgePercent,
  BarChart3,
  Boxes,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ClipboardList,
  CreditCard,
  Download,
  Edit3,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Image,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Package,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Store,
  TrendingUp,
  Truck,
  Trash2,
  Users,
  WalletCards,
  X,
} from 'lucide-react';
import { AdminRecord, AdminWorkspace, managementApi, SalesReport, SiteSettings } from './management-api';
import './admin-functional.css';

type PageKey = 'dashboard' | 'reports' | 'products' | 'orders' | 'inventory' | 'customers' | 'users' | 'marketing' | 'payments' | 'shipping' | 'content' | 'branding';
type EditorKind = 'product' | 'category' | 'inventory' | 'user' | 'role' | 'banner' | 'coupon' | 'payment' | 'shipping' | 'page';

interface Field {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'select' | 'checkbox' | 'color' | 'password' | 'datetime-local' | 'variants' | 'permissions';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string; group?: string }>;
  wide?: boolean;
  upload?: 'single' | 'append';
  hint?: string;
}

interface EditorState {
  kind: EditorKind;
  item?: AdminRecord;
}

const defaultLogoUrl = '/brand-logo-web.jpg';
const defaultSettings: SiteSettings = {
  logoUrl: defaultLogoUrl,
  siteBackgroundUrl: '',
  heroBackgroundUrl: '',
  fontFamily: 'tajawal',
  hideOutOfStockProducts: false,
  whatsapp: 'https://wa.me/967775600074',
  facebook: 'https://www.facebook.com/KingMall1',
  instagram: 'https://www.instagram.com/king1mall',
  threads: 'https://www.threads.net/@king1mall?igshid=MzRlODBiNWFlZA==',
  telegram: 'https://t.me/kingmall3',
  branches: [],
};

const emptyWorkspace: AdminWorkspace = {
  sessionUserId: '',
  sessionRole: '',
  sessionPermissions: [],
  permissionCatalog: [],
  summary: { todaySales: 0, orders: 0, products: 0, customers: 0, stock: 0, lowStock: 0, outOfStock: 0, hiddenProducts: 0 },
  products: [],
  categories: [],
  orders: [],
  customers: [],
  inventory: { stock: [], movements: [] },
  payments: [],
  shipping: [],
  banners: [],
  coupons: [],
  users: [],
  roles: [],
  pages: [],
  settings: defaultSettings,
  analytics: { totals: { daily: 0, monthly: 0, yearly: 0 }, byDay: [], topProducts: [], topCustomers: [] },
};

const pageTitles: Record<PageKey, { title: string; subtitle: string }> = {
  dashboard: { title: 'نظرة عامة', subtitle: 'ملخص مباشر لحالة المتجر' },
  reports: { title: 'تقارير المبيعات', subtitle: 'مراجعة المبيعات والطلبات والعملاء الجدد لأي فترة' },
  products: { title: 'المنتجات والأقسام', subtitle: 'إضافة المنتجات وتنظيم الأقسام والتحكم بالظهور' },
  orders: { title: 'الطلبات', subtitle: 'متابعة الطلبات وتحديث مراحل التنفيذ' },
  inventory: { title: 'المخزون', subtitle: 'الأرصدة الفعلية وسجل حركات المخزون' },
  customers: { title: 'العملاء', subtitle: 'سجل العملاء وقيمة مشترياتهم' },
  users: { title: 'المستخدمون والصلاحيات', subtitle: 'حسابات فريق الإدارة وأدوار الوصول' },
  marketing: { title: 'العروض والبانرات', subtitle: 'إدارة الحملات والكوبونات ومحتوى الواجهة' },
  payments: { title: 'وسائل الدفع', subtitle: 'تفعيل طرق الدفع وتحرير التعليمات' },
  shipping: { title: 'الشحن والتوصيل', subtitle: 'المناطق والرسوم ومدة التوصيل' },
  content: { title: 'إدارة الصفحات', subtitle: 'تحرير الصفحات الثابتة المنشورة في التطبيق' },
  branding: { title: 'هوية المتجر', subtitle: 'تغيير شعار كنج مول من جهازك' },
};

const navGroups = [
  {
    label: 'الرئيسية',
    items: [{ key: 'dashboard' as PageKey, label: 'نظرة عامة', icon: LayoutDashboard }],
  },
  {
    label: 'المتجر',
    items: [
      { key: 'reports' as PageKey, label: 'تقارير المبيعات', icon: BarChart3 },
      { key: 'products' as PageKey, label: 'المنتجات والأقسام', icon: Store },
      { key: 'orders' as PageKey, label: 'الطلبات', icon: ClipboardList },
      { key: 'inventory' as PageKey, label: 'المخزون', icon: Boxes },
      { key: 'customers' as PageKey, label: 'العملاء', icon: Users },
    ],
  },
  {
    label: 'التشغيل',
    items: [
      { key: 'users' as PageKey, label: 'المستخدمون والصلاحيات', icon: ShieldCheck },
      { key: 'marketing' as PageKey, label: 'العروض والبانرات', icon: BadgePercent },
      { key: 'payments' as PageKey, label: 'وسائل الدفع', icon: WalletCards },
      { key: 'shipping' as PageKey, label: 'الشحن والتوصيل', icon: Truck },
      { key: 'content' as PageKey, label: 'إدارة الصفحات', icon: FileText },
      { key: 'branding' as PageKey, label: 'هوية المتجر والشعار', icon: Image },
    ],
  },
];

const orderStatusLabels: Record<string, string> = {
  pending: 'جديد',
  confirmed: 'مؤكد',
  preparing: 'قيد التجهيز',
  shipped: 'تم الشحن',
  delivered: 'تم التسليم',
  cancelled: 'ملغي',
};

const orderTransitions: Record<string, string[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: [],
};

const movementLabels: Record<string, string> = {
  purchase: 'توريد',
  sale: 'بيع',
  return: 'مرتجع',
  adjustment: 'تسوية',
  transfer: 'تحويل',
  reservation: 'حجز',
  release: 'تحرير حجز',
};

const roleLabels: Record<string, string> = {
  admin: 'مدير عام',
  catalog: 'إدارة المنتجات',
  orders: 'إدارة الطلبات',
  marketing: 'التسويق',
  support: 'خدمة العملاء',
  customer: 'عميل',
};

const pagePermissions: Record<PageKey, string[]> = {
  dashboard: ['dashboard.read'],
  reports: ['analytics.read'],
  products: ['products.read', 'products.manage', 'categories.read', 'categories.manage'],
  orders: ['orders.read', 'orders.manage'],
  inventory: ['inventory.read', 'inventory.manage'],
  customers: ['customers.read'],
  users: ['users.read', 'users.manage', 'roles.read', 'roles.manage'],
  marketing: ['banners.read', 'banners.manage', 'coupons.read', 'coupons.manage'],
  payments: ['payments.read', 'payments.manage'],
  shipping: ['shipping.read', 'shipping.manage'],
  content: ['pages.read', 'pages.manage'],
  branding: ['settings.manage'],
};

function permissionGranted(granted: string[], required: string) {
  return granted.includes('*') || granted.includes(required) || granted.includes(`${required.split('.')[0]}.*`);
}

function formatYER(value: number) {
  return new Intl.NumberFormat('ar-YE', { maximumFractionDigits: 0 }).format(Number(value || 0)) + ' ر.ي';
}

function formatDate(value?: string) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ar-YE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function yemeniDateInput(value = new Date()) {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Aden', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${read('year')}-${read('month')}-${read('day')}`;
}

function localDateTimeInput(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Aden', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || '';
  return `${read('year')}-${read('month')}-${read('day')}T${read('hour')}:${read('minute')}`;
}

function EmptyState({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <Package size={34} />
      <strong>{title}</strong>
      <span>{text}</span>
      {action}
    </div>
  );
}

function SectionHeader({ title, note, actions }: { title: string; note?: string; actions?: ReactNode }) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {note && <p>{note}</p>}
      </div>
      <div className="section-actions">{actions}</div>
    </div>
  );
}

function EntityModal({
  title,
  item,
  fields,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  item?: AdminRecord;
  fields: Field[];
  saving: boolean;
  onClose: () => void;
  onSave: (values: AdminRecord) => Promise<void>;
}) {
  const [values, setValues] = useState<AdminRecord>(item ?? {});
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState('');

  async function upload(field: Field, files: File[]) {
    if (!files.length) return;
    setError('');
    setUploading(field.name);
    try {
      const results = await Promise.all(files.map((file) => managementApi.uploadImage(file)));
      setValues((current) => ({
        ...current,
        [field.name]: field.upload === 'append'
          ? [String(current[field.name] || '').trim(), ...results.map((result) => result.url)].filter(Boolean).join('\n')
          : results[0]?.url || '',
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر رفع الصورة.');
    } finally {
      setUploading('');
    }
  }

  function imageUrls(field: Field) {
    return String(values[field.name] || '').split(/\r?\n/).map((value) => value.trim()).filter(Boolean);
  }

  function setImageUrls(field: Field, urls: string[]) {
    setValues((current) => ({ ...current, [field.name]: urls.join('\n') }));
  }

  function moveImage(field: Field, index: number, direction: -1 | 1) {
    const urls = imageUrls(field);
    const target = index + direction;
    if (target < 0 || target >= urls.length) return;
    const current = urls[index]!;
    urls[index] = urls[target]!;
    urls[target] = current;
    setImageUrls(field, urls);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    const payload: AdminRecord = { ...values };
    fields.forEach((field) => {
      if (field.type === 'number' && payload[field.name] !== '' && payload[field.name] != null) payload[field.name] = Number(payload[field.name]);
      if (field.type === 'checkbox') payload[field.name] = Boolean(payload[field.name]);
    });
    try {
      await onSave(payload);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر الحفظ.');
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && onClose()}>
      <form className="entity-modal" onSubmit={submit}>
        <div className="modal-heading">
          <div>
            <span>كنج مول</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="إغلاق"><X size={20} /></button>
        </div>
        <div className="form-grid">
          {fields.map((field) => {
            if (field.type === 'permissions') {
              const selected = Array.isArray(values[field.name]) ? values[field.name] as string[] : [];
              const groups = Object.entries((field.options ?? []).reduce<Record<string, Array<{ value: string; label: string }>>>((result, option) => {
                const group = option.group || 'صلاحيات أخرى';
                (result[group] ??= []).push(option);
                return result;
              }, {}));
              const togglePermission = (permission: string, checked: boolean) => setValues((current) => ({
                ...current,
                [field.name]: checked ? [...new Set([...selected, permission])] : selected.filter((item) => item !== permission),
              }));
              return <section className="permission-picker wide" key={field.name}>
                <div><strong>{field.label}{field.required && ' *'}</strong><small>الصلاحيات تطبق في الخادم فور الحفظ، حتى على الجلسات المفتوحة.</small></div>
                {groups.map(([group, options]) => <fieldset key={group}><legend>{group}</legend><div>{options.map((option) => <label key={option.value}>
                  <input type="checkbox" checked={selected.includes(option.value)} onChange={(event) => togglePermission(option.value, event.target.checked)} />
                  <span>{option.label}</span>
                </label>)}</div></fieldset>)}
              </section>;
            }
            if (field.type === 'variants') {
              const variants = Array.isArray(values[field.name]) ? values[field.name] as AdminRecord[] : [];
              const totalStock = variants.reduce((sum, variant) => sum + Number(variant.stock || 0), 0);
              const updateVariant = (index: number, key: string, value: unknown) => setValues((current) => ({
                ...current,
                [field.name]: variants.map((variant, variantIndex) => variantIndex === index ? { ...variant, [key]: value } : variant),
              }));
              const addColor = () => setValues((current) => ({
                ...current,
                [field.name]: [...variants, { barcode: '', stock: 0, color: '', colorHex: '#111827', size: 'موحد' }],
              }));
              const addSize = () => {
                const previous = variants.at(-1);
                setValues((current) => ({
                  ...current,
                  [field.name]: [...variants, { barcode: '', stock: 0, color: previous?.color || 'افتراضي', colorHex: previous?.colorHex || '#111827', size: '' }],
                }));
              };
              return <section className="variant-builder wide" key={field.name}>
                <div className="variant-builder-heading"><div><strong>{field.label}{field.required && ' *'}</strong><small>كل سطر يمثل لونًا ومقاسًا بمخزونه الحقيقي. الألوان المختلفة تظهر للعميل كخيارات منفصلة. إجمالي المخزون: {totalStock.toLocaleString('ar-YE')} قطعة.</small></div><span className="variant-builder-actions"><button type="button" className="button secondary" onClick={addSize}><Plus size={16} /> مقاس لنفس اللون</button><button type="button" className="button secondary" onClick={addColor}><Plus size={16} /> لون جديد</button></span></div>
                <div className="variant-labels"><span>الباركود (اختياري)</span><span>اللون</span><span>رمز اللون</span><span>المقاس</span><span>المخزون</span><span /></div>
                <div className="variant-rows">{variants.map((variant, index) => <div className="variant-row" key={variant.id || `new-${index}`}>
                  <input inputMode="numeric" aria-label={`باركود الخيار ${index + 1}`} placeholder="امسح أو اكتب الباركود" value={variant.barcode ?? ''} onChange={(event) => updateVariant(index, 'barcode', event.target.value)} />
                  <input required aria-label={`اللون ${index + 1}`} placeholder="أسود" value={variant.color ?? ''} onChange={(event) => updateVariant(index, 'color', event.target.value)} />
                  <input required aria-label={`رمز اللون ${index + 1}`} type="color" value={variant.colorHex || '#111827'} onChange={(event) => updateVariant(index, 'colorHex', event.target.value)} />
                  <input required aria-label={`المقاس ${index + 1}`} placeholder="M" value={variant.size ?? ''} onChange={(event) => updateVariant(index, 'size', event.target.value)} />
                  <input required aria-label={`المخزون ${index + 1}`} type="number" min="0" step="1" value={variant.stock ?? 0} onChange={(event) => updateVariant(index, 'stock', Number(event.target.value))} />
                  <button type="button" className="variant-remove" disabled={variants.length === 1} onClick={() => setValues((current) => ({ ...current, [field.name]: variants.filter((_, variantIndex) => variantIndex !== index) }))} aria-label={`حذف الخيار ${index + 1}`}><Trash2 size={16} /></button>
                </div>)}</div>
              </section>;
            }
            if (field.type === 'checkbox') {
              return (
                <label className={'checkbox-field ' + (field.wide ? 'wide' : '')} key={field.name}>
                  <input
                    type="checkbox"
                    checked={Boolean(values[field.name])}
                    onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.checked }))}
                  />
                  <span>{field.label}</span>
                </label>
              );
            }
            return (
              <label className={field.wide ? 'wide' : ''} key={field.name}>
                <span>{field.label}{field.required && ' *'}</span>
                {field.upload && (
                  <span className="image-upload">
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple={field.upload === 'append'} disabled={Boolean(uploading)} onChange={(event) => { void upload(field, Array.from(event.target.files ?? [])); event.currentTarget.value = ''; }} />
                    {uploading === field.name ? <><LoaderCircle className="spin" size={16} /> جارٍ رفع الصور...</> : <><Image size={16} /> {field.upload === 'append' ? 'اختر عدة صور دفعة واحدة' : 'اختر صورة من الجهاز'}</>}
                  </span>
                )}
                {field.upload === 'append' && imageUrls(field).length > 0 && <span className="image-preview-grid">{imageUrls(field).map((url, index, urls) => <span className="image-preview-card" key={`${url}-${index}`}>
                  <img src={url} alt={`صورة المنتج ${index + 1}`} />
                  <em>{index === 0 ? 'الرئيسية' : index + 1}</em>
                  <span><button type="button" disabled={index === 0} onClick={() => moveImage(field, index, -1)} aria-label="تحريك الصورة للأعلى"><ChevronUp size={14} /></button><button type="button" disabled={index === urls.length - 1} onClick={() => moveImage(field, index, 1)} aria-label="تحريك الصورة للأسفل"><ChevronDown size={14} /></button><button type="button" onClick={() => setImageUrls(field, urls.filter((_, imageIndex) => imageIndex !== index))} aria-label="حذف الصورة"><Trash2 size={14} /></button></span>
                </span>)}</span>}
                {field.upload === 'single' && imageUrls(field)[0] && <span className="single-image-preview"><img src={imageUrls(field)[0]} alt={`معاينة ${field.label}`} /><button type="button" onClick={() => setImageUrls(field, [])}><Trash2 size={14} /> إزالة الصورة</button></span>}
                {field.type === 'textarea' ? (
                  <textarea
                    required={field.required}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                  />
                ) : field.type === 'select' ? (
                  <select
                    required={field.required}
                    value={values[field.name] ?? ''}
                    onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                  >
                    <option value="">اختر...</option>
                    {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                ) : (
                  <input
                    type={field.type || 'text'}
                    required={field.required}
                    minLength={field.type === 'password' ? 12 : undefined}
                    autoComplete={field.type === 'password' ? 'new-password' : undefined}
                    placeholder={field.placeholder}
                    value={values[field.name] ?? ''}
                    onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                  />
                )}
                {field.hint && <small className="field-hint">{field.hint}</small>}
              </label>
            );
          })}
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>إلغاء</button>
          <button type="submit" className="button primary" disabled={saving}>
            {saving ? <LoaderCircle className="spin" size={18} /> : null}
            حفظ التغييرات
          </button>
        </div>
      </form>
    </div>
  );
}

function LoginScreen({ onLogin, logoUrl }: { onLogin: () => void; logoUrl: string }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await managementApi.login(identifier, password);
      onLogin();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تسجيل الدخول.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-screen" dir="rtl">
      <div className="login-ambient" aria-hidden="true"><span /><span /><span /></div>
      <div className="login-security-badge" aria-hidden="true"><ShieldCheck size={17} /><span>بوابة إدارية محمية</span></div>
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo"><span aria-hidden="true" /><img src={logoUrl} alt="شعار كنج مول الرسمي" /></div>
        <div className="login-copy">
          <span className="eyebrow"><i /> KING MALL ADMIN</span>
          <h1>تسجيل دخول الإدارة</h1>
          <p>مرحبًا بعودتك. ادخل بياناتك لإدارة المتجر بأمان.</p>
        </div>
        <label className="login-field">
          <span><Mail size={14} /> البريد الإلكتروني</span>
          <div className="login-input-shell"><input type="email" value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" placeholder="name@example.com" required /><i aria-hidden="true" /></div>
        </label>
        <label className="login-field">
          <span><LockKeyhole size={14} /> كلمة المرور</span>
          <div className="login-input-shell"><input type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'} aria-pressed={showPassword}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button><i aria-hidden="true" /></div>
        </label>
        {error && <div className="form-error">{error}</div>}
        <button className="button primary login-submit" disabled={loading}>
          {loading && <LoaderCircle className="spin" size={18} />}
          <span>{loading ? 'جارٍ التحقق...' : 'دخول آمن'}</span>
        </button>
        <div className="login-trust"><ShieldCheck size={15} /><span>اتصال مشفّر وجلسة محمية</span></div>
      </form>
    </main>
  );
}

function ChangePasswordModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (newPassword.length < 12) return setError('كلمة المرور الجديدة يجب ألا تقل عن 12 حرفًا.');
    if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
      return setError('استخدم حرفًا كبيرًا وصغيرًا ورقمًا ورمزًا خاصًا على الأقل.');
    }
    if (newPassword !== confirmPassword) return setError('تأكيد كلمة المرور غير مطابق.');
    setLoading(true);
    try {
      await managementApi.changePassword(currentPassword, newPassword);
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تغيير كلمة المرور.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && onClose()}>
      <form className="password-modal" onSubmit={submit} dir="rtl">
        <div className="modal-heading">
          <div><span>أمان الحساب</span><h2>تغيير كلمة المرور</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="إغلاق"><X size={20} /></button>
        </div>
        <p className="password-guidance">بعد الحفظ سيتم تسجيل خروجك، ثم تدخل بكلمة المرور الجديدة.</p>
        <label><span>كلمة المرور الحالية</span><input type={showPasswords ? 'text' : 'password'} autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label>
        <label><span>كلمة المرور الجديدة</span><input type={showPasswords ? 'text' : 'password'} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} required /></label>
        <label><span>تأكيد كلمة المرور الجديدة</span><input type={showPasswords ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={12} required /></label>
        <label className="show-passwords"><input type="checkbox" checked={showPasswords} onChange={(event) => setShowPasswords(event.target.checked)} /><span>إظهار كلمات المرور</span></label>
        <small>12 حرفًا على الأقل، وتحتوي على حرف كبير وصغير ورقم ورمز خاص.</small>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="button secondary" onClick={onClose}>إلغاء</button>
          <button type="submit" className="button primary" disabled={loading}>{loading && <LoaderCircle className="spin" size={18} />}{loading ? 'جارٍ التغيير...' : 'حفظ كلمة المرور'}</button>
        </div>
      </form>
    </div>
  );
}

function OrderDetailsModal({ order, onClose }: { order: AdminRecord; onClose: () => void }) {
  const items = Array.isArray(order.items) ? order.items : [];
  const history = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && onClose()}>
      <section className="order-details-modal" role="dialog" aria-modal="true" aria-labelledby="order-details-title" dir="rtl">
        <div className="modal-heading">
          <div><span>تفاصيل الطلب</span><h2 id="order-details-title">الطلب #{order.id}</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="إغلاق"><X size={20} /></button>
        </div>
        <div className="order-details-body">
          <div className="order-details-summary">
            <article><span>الحالة</span><strong className={'order-state ' + order.status}>{orderStatusLabels[order.status] || order.status}</strong></article>
            <article><span>تاريخ الطلب</span><strong>{formatDate(order.createdAt)}</strong></article>
            <article><span>وسيلة الدفع</span><strong>{order.paymentMethod || '—'}</strong></article>
            <article><span>الإجمالي</span><strong>{formatYER(order.total)}</strong></article>
          </div>

          <div className="order-details-grid">
            <article>
              <h3><Users size={17} /> بيانات العميل</h3>
              <dl><div><dt>الاسم</dt><dd>{order.customerName}</dd></div><div><dt>الهاتف</dt><dd dir="ltr">{order.customerPhone}</dd></div>{order.customerEmail && <div><dt>البريد</dt><dd>{order.customerEmail}</dd></div>}</dl>
            </article>
            <article>
              <h3><MapPin size={17} /> عنوان التوصيل</h3>
              <dl><div><dt>المحافظة والمنطقة</dt><dd>{[order.city, order.district].filter(Boolean).join(' · ') || '—'}</dd></div><div><dt>العنوان</dt><dd>{order.address || '—'}</dd></div></dl>
              {order.deliveryLocation?.mapUrl && <a className="order-map-link" href={order.deliveryLocation.mapUrl} target="_blank" rel="noopener noreferrer"><MapPin size={15} /> فتح موقع العميل على الخريطة <ExternalLink size={12} /></a>}
            </article>
          </div>

          <section className="order-items-section">
            <h3><Package size={18} /> المنتجات ({order.itemCount || items.reduce((sum: number, item: AdminRecord) => sum + Number(item.quantity || 0), 0)})</h3>
            {items.length ? <div className="order-item-list">{items.map((item: AdminRecord, index: number) => (
              <article key={item.id || `${item.sku}-${index}`}>
                <div className="order-item-image">{item.image ? <img src={item.image} alt={item.productName} /> : <Package size={22} />}</div>
                <div className="order-item-copy"><strong>{item.productName}</strong><span>SKU: <b dir="ltr">{item.sku || '—'}</b></span><small>{[item.color && `اللون: ${item.color}`, item.size && `المقاس: ${item.size}`].filter(Boolean).join(' · ') || 'لا توجد خيارات محفوظة'}</small></div>
                <div className="order-item-numbers"><span>{Number(item.quantity)} × {formatYER(Number(item.unitPrice))}</span><strong>{formatYER(Number(item.lineTotal ?? Number(item.unitPrice) * Number(item.quantity)))}</strong></div>
              </article>
            ))}</div> : <div className="order-detail-empty">بيانات أصناف هذا الطلب غير محفوظة في النسخة القديمة.</div>}
          </section>

          <div className="order-financials">
            <div><span>المجموع الفرعي</span><strong>{formatYER(Number(order.subtotal ?? order.total))}</strong></div>
            <div><span>الخصم{order.couponCode ? ` (${order.couponCode})` : ''}</span><strong>- {formatYER(Number(order.discount || 0))}</strong></div>
            <div><span>رسوم التوصيل</span><strong>{formatYER(Number(order.shippingFee || 0))}</strong></div>
            <div className="total"><span>إجمالي الطلب</span><strong>{formatYER(Number(order.total))}</strong></div>
          </div>

          {order.notes && <section className="order-notes"><h3>ملاحظات العميل</h3><p>{order.notes}</p></section>}
          {history.length > 0 && <section className="order-history"><h3><ClipboardList size={17} /> سجل الحالة</h3><div>{history.map((event: AdminRecord, index: number) => <article key={`${event.createdAt}-${index}`}><i /><span><strong>{orderStatusLabels[event.toStatus] || event.toStatus}</strong><small>{formatDate(event.createdAt)}{event.actorName ? ` · بواسطة ${event.actorName}` : ''}{event.note ? ` · ${event.note}` : ''}</small></span></article>)}</div></section>}
        </div>
      </section>
    </div>
  );
}

export default function AdminApp() {
  const initialReportTo = yemeniDateInput();
  const initialReportFrom = `${initialReportTo.slice(0, 8)}01`;
  const [authenticated, setAuthenticated] = useState(managementApi.authenticated());
  const [workspace, setWorkspace] = useState<AdminWorkspace>(emptyWorkspace);
  const [page, setPage] = useState<PageKey>('dashboard');
  const [loading, setLoading] = useState(authenticated);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [search, setSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<AdminRecord | null>(null);
  const [logoUrl, setLogoUrl] = useState(defaultLogoUrl);
  const [logoUploading, setLogoUploading] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState<SiteSettings>(defaultSettings);
  const [currentRole, setCurrentRole] = useState(managementApi.role() || 'support');
  const [reportRange, setReportRange] = useState({ from: initialReportFrom, to: initialReportTo });
  const [salesReport, setSalesReport] = useState<SalesReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const permissions = workspace.sessionPermissions ?? [];
  const can = (permission: string) => permissionGranted(permissions, permission);
  const allowedPages = useMemo(() => (Object.keys(pagePermissions) as PageKey[]).filter((key) => pagePermissions[key].some(can)), [permissions]);
  const canUpdateOrders = can('orders.manage');

  const title = pageTitles[page];
  const adminName = sessionStorage.getItem('km-admin-name') || 'مدير النظام';
  const currentRoleName = workspace.roles.find((role) => role.id === currentRole)?.name || roleLabels[currentRole] || 'موظف';

  async function refresh(showLoader = true) {
    if (!managementApi.authenticated()) return;
    if (showLoader) setLoading(true);
    setError('');
    try {
      const nextWorkspace = await managementApi.workspace();
      setWorkspace(nextWorkspace);
      if (nextWorkspace.sessionRole) {
        sessionStorage.setItem('km-admin-role', nextWorkspace.sessionRole);
        setCurrentRole(nextWorkspace.sessionRole);
      }
      setLogoUrl(nextWorkspace.settings?.logoUrl || defaultLogoUrl);
      setSettingsDraft(nextWorkspace.settings || defaultSettings);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'تعذر تحميل البيانات.';
      setError(message);
      if (!managementApi.authenticated()) setAuthenticated(false);
    } finally {
      if (showLoader) setLoading(false);
    }
  }

  async function loadSalesReport(range = reportRange) {
    if (!range.from || !range.to) return;
    setReportLoading(true);
    setError('');
    try {
      setSalesReport(await managementApi.salesReport(range.from, range.to));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تحميل تقرير المبيعات.');
    } finally {
      setReportLoading(false);
    }
  }

  function openSalesReport(from: string, to: string) {
    const range = { from, to };
    setReportRange(range);
    setPage('reports');
    void loadSalesReport(range);
  }

  useEffect(() => {
    if (authenticated) void refresh();
  }, [authenticated]);

  useEffect(() => {
    managementApi.siteSettings().then((settings) => setLogoUrl(settings?.logoUrl || defaultLogoUrl)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const sessionExpired = () => {
      setAuthenticated(false);
      setWorkspace(emptyWorkspace);
      setEditor(null);
      setSelectedOrder(null);
      setPasswordDialog(false);
    };
    globalThis.addEventListener('km-admin-session-expired', sessionExpired);
    return () => globalThis.removeEventListener('km-admin-session-expired', sessionExpired);
  }, []);

  useEffect(() => {
    const permissionsChanged = () => { if (authenticated) void refresh(false); };
    globalThis.addEventListener('km-admin-permissions-changed', permissionsChanged);
    return () => globalThis.removeEventListener('km-admin-permissions-changed', permissionsChanged);
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && allowedPages.length && !allowedPages.includes(page)) setPage(allowedPages[0]!);
  }, [authenticated, allowedPages, page]);

  useEffect(() => {
    if (authenticated && page === 'reports' && !salesReport && !reportLoading) void loadSalesReport();
  }, [authenticated, page]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return workspace.products;
    return workspace.products.filter((product) =>
      [product.name, product.nameEn, product.brand, product.sku]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [search, workspace.products]);

  const visibleCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    if (!query) return workspace.customers;
    return workspace.customers.filter((customer) =>
      [customer.name, customer.phone, customer.email, customer.country, customer.governorate, customer.district, customer.street, customer.addressDetails]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [customerSearch, workspace.customers]);

  function downloadCustomerReport() {
    const rows = visibleCustomers.map((customer) => [
      customer.name, customer.phone, customer.email, customer.phoneVerified ? 'موثق' : 'غير موثق', customer.country,
      customer.governorate, customer.district, customer.street, customer.addressDetails, customer.orders, customer.total, customer.joined,
    ]);
    const escapeCell = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
    const csv = [['الاسم', 'الهاتف', 'البريد', 'توثيق الهاتف', 'الدولة', 'المحافظة', 'المديرية أو المنطقة', 'الشارع', 'العنوان التفصيلي', 'الطلبات', 'إجمالي المشتريات', 'تاريخ التسجيل'], ...rows]
      .map((row) => row.map(escapeCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `kingmall-registered-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function notify(message: string) {
    setToast(message);
  }

  function prepare(kind: EditorKind, item?: AdminRecord): AdminRecord | undefined {
    if (!item) {
      if (kind === 'product') return { active: true, sku: '', brand: 'King Collection', imagesText: '', variants: [{ barcode: '', stock: 0, color: 'افتراضي', colorHex: '#111827', size: 'موحد' }] };
      if (kind === 'category') return { active: true, color: '#F3F4F6', icon: '•', position: 0 };
      if (kind === 'inventory') return { type: 'adjustment', quantity: 1 };
      if (kind === 'user') return { active: true, role: 'support' };
      if (kind === 'role') return { active: true, permissions: ['admin.access'] };
      if (kind === 'banner') return { active: true, background: '#111827', foreground: '#FFFFFF', placement: 'home_slider', position: 0 };
      if (kind === 'coupon') return { active: true, type: 'percent', value: 0, minimum: 0 };
      if (kind === 'payment') return { enabled: true, mode: 'manual', icon: 'wallet', color: '#0F766E' };
      if (kind === 'shipping') return { active: true, fee: 0 };
      if (kind === 'page') return { published: true };
      return undefined;
    }
    if (kind === 'product') {
      return { ...item, imagesText: (item.images ?? []).join('\n'), variants: (item.variants ?? []).map((variant: AdminRecord) => ({ ...variant })) };
    }
    if (kind === 'shipping') return { ...item, districtsText: (item.districts ?? []).join('، ') };
    if (kind === 'coupon') return { ...item, startsAt: item.startsAt?.slice(0, 16) ?? '', endsAt: item.endsAt?.slice(0, 16) ?? '' };
    if (kind === 'banner') return { ...item, startsAt: localDateTimeInput(item.startsAt), endsAt: localDateTimeInput(item.endsAt) };
    return { ...item };
  }

  function openEditor(kind: EditorKind, item?: AdminRecord) {
    setEditor({ kind, item: prepare(kind, item) });
  }

  async function execute(action: () => Promise<unknown>, success: string) {
    setSaving(true);
    try {
      await action();
      setEditor(null);
      await refresh(false);
      notify(success);
    } finally {
      setSaving(false);
    }
  }

  async function saveEditor(values: AdminRecord) {
    if (!editor) return;
    const originalId = editor.item?.id as string | undefined;
    if (editor.kind === 'product') {
      const original = workspace.products.find((item) => item.id === originalId);
      const variants = (Array.isArray(values.variants) ? values.variants : []).map((variant: AdminRecord) => ({
        ...variant,
        barcode: String(variant.barcode || '').trim(),
        stock: Number(variant.stock || 0),
        color: String(variant.color || '').trim(),
        colorHex: String(variant.colorHex || '#111827'),
        size: String(variant.size || '').trim(),
      }));
      const sku = String(values.sku || '').trim();
      if (!sku) throw new Error('أدخل رمز SKU واحدًا للمنتج.');
      if (!variants.length) throw new Error('أضف لونًا أو مقاسًا واحدًا على الأقل.');
      if (variants.some((variant: AdminRecord) => !variant.color || !variant.size || variant.stock < 0)) throw new Error('أكمل اللون والمقاس لكل خيار وتأكد أن المخزون صفر أو أكثر.');
      const barcodes = variants.map((variant: AdminRecord) => variant.barcode.toLowerCase()).filter(Boolean);
      if (new Set(barcodes).size !== barcodes.length) throw new Error('الباركود يجب أن يكون مختلفًا لكل خيار.');
      const combinations = variants.map((variant: AdminRecord) => `${variant.color.toLowerCase()}::${variant.size.toLowerCase()}`);
      if (new Set(combinations).size !== combinations.length) throw new Error('يوجد لون ومقاس مكرر. اترك لكل تركيبة سطرًا واحدًا وحدد مخزونها.');
      const images = String(values.imagesText || '').split(/\r?\n/).map((value) => value.trim()).filter(Boolean);
      if (values.active && images.length === 0) throw new Error('أضف صورة واحدة على الأقل قبل إظهار المنتج في المتجر.');
      const payload: AdminRecord = {
        sku,
        name: values.name,
        nameEn: values.nameEn || '',
        description: values.description || '',
        price: Number(values.price),
        compareAtPrice: values.compareAtPrice === '' ? null : Number(values.compareAtPrice || 0),
        categoryId: values.categoryId,
        brand: values.brand || 'King Collection',
        images,
        badges: original?.badges ?? [],
        active: Boolean(values.active),
        variants,
      };
      return execute(() => managementApi.save('products', originalId, payload), originalId ? 'تم تحديث المنتج.' : 'تمت إضافة المنتج.');
    }
    if (editor.kind === 'category') {
      const payload = { ...values, parentId: values.parentId || null };
      return execute(() => managementApi.save('categories', originalId, payload), originalId ? 'تم تحديث القسم.' : 'تمت إضافة القسم.');
    }
    if (editor.kind === 'inventory') {
      return execute(() => managementApi.addInventoryMovement({
        variantId: values.variantId,
        type: values.type,
        quantity: Number(values.quantity),
        note: values.note || '',
      }), 'تم تسجيل حركة المخزون وتحديث الرصيد.');
    }
    if (editor.kind === 'user') {
      const password = String(values.password || '');
      if (!originalId && !password) throw new Error('أدخل كلمة مرور للمستخدم الجديد.');
      if (password && password.length < 12) throw new Error('كلمة المرور يجب أن تكون 12 حرفًا على الأقل.');
      if (password && (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password))) {
        throw new Error('كلمة المرور يجب أن تحتوي حرفًا إنجليزيًا كبيرًا وصغيرًا ورقمًا ورمزًا مثل @ أو #.');
      }
      const branch = String(values.branch || '').trim();
      const payload: AdminRecord = {
        ...values,
        name: String(values.name || '').trim(),
        email: String(values.email || '').trim().toLowerCase(),
        phone: String(values.phone || '').replace(/[\s-]/g, ''),
        branch: !branch || branch === 'كل الفروع' ? null : branch,
      };
      if (!password) delete payload.password;
      return execute(() => managementApi.save('users', originalId, payload), originalId ? 'تم تحديث المستخدم.' : 'تمت إضافة المستخدم.');
    }
    if (editor.kind === 'role') {
      const payload = { ...values, permissions: Array.isArray(values.permissions) ? values.permissions : [] };
      return execute(() => managementApi.save('roles', originalId, payload), originalId ? 'تم تحديث الدور وتطبيق صلاحياته فورًا.' : 'تم إنشاء الدور الجديد.');
    }
    if (editor.kind === 'banner') {
      const payload = {
        ...values,
        position: Number(values.position || 0),
        startsAt: values.startsAt ? new Date(values.startsAt).toISOString() : null,
        endsAt: values.endsAt ? new Date(values.endsAt).toISOString() : null,
      };
      return execute(() => managementApi.save('banners', originalId, payload), originalId ? 'تم تحديث البانر.' : 'تمت إضافة البانر.');
    }
    if (editor.kind === 'coupon') {
      const payload = { ...values, limit: values.limit === '' ? null : Number(values.limit || 0), startsAt: values.startsAt || null, endsAt: values.endsAt || null };
      return execute(() => managementApi.save('coupons', originalId, payload), originalId ? 'تم تحديث الكوبون.' : 'تمت إضافة الكوبون.');
    }
    if (editor.kind === 'payment') {
      return execute(() => managementApi.save('payments', originalId, values), originalId ? 'تم تحديث وسيلة الدفع.' : 'تمت إضافة وسيلة الدفع.');
    }
    if (editor.kind === 'shipping') {
      const payload = { ...values, districts: String(values.districtsText || '').split(/[،,]/).map((value) => value.trim()).filter(Boolean) };
      return execute(() => managementApi.save('shipping-zones', originalId, payload), originalId ? 'تم تحديث منطقة الشحن.' : 'تمت إضافة منطقة الشحن.');
    }
    if (editor.kind === 'page') {
      return execute(() => managementApi.save('pages', originalId, values), originalId ? 'تم تحديث الصفحة.' : 'تمت إضافة الصفحة.');
    }
  }

  async function remove(resource: string, id: string, label: string) {
    if (!window.confirm('هل تريد بالتأكيد إخفاء أو حذف ' + label + '؟')) return;
    try {
      await execute(() => managementApi.remove(resource, id), 'تم تنفيذ العملية بنجاح.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تنفيذ العملية.');
    }
  }

  async function setProductVisibility(product: AdminRecord, active: boolean) {
    const action = active ? 'إظهار' : 'إخفاء';
    if (!window.confirm(`هل تريد ${action} «${product.name}» ${active ? 'للزبائن' : 'من المتجر'}؟`)) return;
    try {
      await execute(() => managementApi.save('products', product.id, { active }), active ? 'تم إظهار المنتج للزبائن.' : 'تم إخفاء المنتج مع الاحتفاظ بسجلاته.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : `تعذر ${action} المنتج.`);
    }
  }

  async function deleteProductPermanently(product: AdminRecord) {
    if (!window.confirm(`حذف نهائي لـ «${product.name}»؟\nلن يُسمح بالحذف إذا كان المنتج مرتبطًا بطلب أو حركة مخزون.`)) return;
    try {
      await execute(() => managementApi.deleteProductPermanently(product.id), 'تم حذف المنتج نهائيًا.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر حذف المنتج نهائيًا.');
    }
  }

  async function updateOrder(id: string, status: string) {
    try {
      await execute(() => managementApi.updateOrder(id, status), 'تم تحديث حالة الطلب.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تحديث الطلب.');
    }
  }

  function logout() {
    managementApi.logout();
    setAuthenticated(false);
    setWorkspace(emptyWorkspace);
  }

  const editorDefinition = useMemo(() => {
    if (!editor) return null;
    const categoryLabel = (item: AdminRecord) => {
      const parent = workspace.categories.find((candidate) => candidate.id === item.parentId);
      return parent ? `${parent.name} › ${item.name}` : item.name;
    };
    const categoryOptions = workspace.categories
      .filter((item) => item.active)
      .map((item) => ({ value: item.id, label: categoryLabel(item) }))
      .sort((first, second) => first.label.localeCompare(second.label, 'ar'));
    const parentCategoryOptions = [
      { value: '', label: 'قسم رئيسي' },
      ...workspace.categories
        .filter((item) => item.id !== editor.item?.id)
        .map((item) => ({ value: item.id, label: categoryLabel(item) }))
        .sort((first, second) => first.label.localeCompare(second.label, 'ar')),
    ];
    const variantOptions = workspace.inventory.stock.filter((item) => item.active).map((item) => ({ value: item.id, label: item.productName + ' — ' + item.sku + ' (الرصيد ' + item.stock + ')' }));
    const roleOptions = workspace.roles.filter((item) => item.id !== 'customer' && item.active !== false).map((item) => ({ value: item.id, label: item.name }));
    const permissionOptions = workspace.permissionCatalog.map((permission) => ({ value: permission.id, label: permission.label, group: permission.group }));
    const definitions: Record<EditorKind, { title: string; fields: Field[] }> = {
      product: {
        title: editor.item?.id ? 'تعديل المنتج' : 'إضافة منتج جديد',
        fields: [
          { name: 'name', label: 'اسم المنتج بالعربية', required: true },
          { name: 'nameEn', label: 'الاسم بالإنجليزية' },
          { name: 'sku', label: 'رمز SKU للمنتج', required: true, placeholder: 'مثال: KM-00125', hint: 'رمز واحد للمنتج كله؛ لا تحتاج إلى رمز مختلف لكل لون أو مقاس.' },
          { name: 'categoryId', label: 'القسم', type: 'select', required: true, options: categoryOptions },
          { name: 'brand', label: 'العلامة التجارية' },
          { name: 'price', label: 'سعر البيع', type: 'number', required: true },
          { name: 'compareAtPrice', label: 'السعر قبل الخصم', type: 'number' },
          { name: 'imagesText', label: 'صور المنتج (يمكن رفع أكثر من صورة)', type: 'textarea', upload: 'append', wide: true },
          { name: 'variants', label: 'الألوان والمقاسات والمخزون', type: 'variants', required: true, wide: true },
          { name: 'description', label: 'وصف المنتج', type: 'textarea', wide: true },
          { name: 'active', label: 'إظهار المنتج في المتجر', type: 'checkbox', wide: true },
        ],
      },
      category: {
        title: editor.item?.id ? 'تعديل القسم' : 'إضافة قسم جديد',
        fields: [
          { name: 'name', label: 'اسم القسم', required: true },
          { name: 'nameEn', label: 'الاسم بالإنجليزية' },
          { name: 'parentId', label: 'يتبع القسم', type: 'select', options: parentCategoryOptions },
          { name: 'position', label: 'ترتيب الظهور', type: 'number' },
          { name: 'icon', label: 'الرمز' },
          { name: 'color', label: 'لون القسم', type: 'color' },
          { name: 'image', label: 'صورة القسم', upload: 'single', wide: true },
          { name: 'active', label: 'إظهار القسم', type: 'checkbox', wide: true },
        ],
      },
      inventory: {
        title: 'تسجيل حركة مخزون',
        fields: [
          { name: 'variantId', label: 'الصنف', type: 'select', required: true, options: variantOptions, wide: true },
          { name: 'type', label: 'نوع الحركة', type: 'select', required: true, options: [
            { value: 'purchase', label: 'توريد جديد' },
            { value: 'return', label: 'مرتجع عميل' },
            { value: 'adjustment', label: 'تسوية جرد' },
            { value: 'transfer', label: 'تحويل' },
            { value: 'release', label: 'تحرير حجز' },
          ] },
          { name: 'quantity', label: 'الكمية (+ إضافة / - خصم)', type: 'number', required: true },
          { name: 'note', label: 'ملاحظة الحركة', type: 'textarea', wide: true },
        ],
      },
      user: {
        title: editor.item?.id ? 'تعديل المستخدم' : 'إضافة مستخدم',
        fields: [
          { name: 'name', label: 'الاسم الكامل', required: true },
          { name: 'email', label: 'البريد الإلكتروني', type: 'text' },
          { name: 'phone', label: 'رقم الهاتف' },
          { name: 'role', label: 'الدور والصلاحية', type: 'select', required: true, options: roleOptions },
          { name: 'branch', label: 'الفرع', placeholder: 'اتركه فارغًا لكل الفروع', hint: 'اترك الحقل فارغًا إذا كان المستخدم يعمل في كل الفروع.' },
          { name: 'password', label: editor.item?.id ? 'كلمة مرور جديدة (اختياري)' : 'كلمة المرور', type: 'password', required: !editor.item?.id, placeholder: '12 حرفًا على الأقل', hint: '12 حرفًا على الأقل، وتتضمن حرفًا كبيرًا وصغيرًا ورقمًا ورمزًا مثل @ أو #.' },
          { name: 'active', label: 'الحساب نشط', type: 'checkbox', wide: true },
        ],
      },
      role: {
        title: editor.item?.id ? 'تعديل الدور والصلاحيات' : 'إنشاء دور جديد',
        fields: [
          { name: 'name', label: 'اسم الدور بالعربية', required: true },
          { name: 'description', label: 'وصف واضح لمسؤوليات الدور', type: 'textarea', required: true, wide: true },
          { name: 'permissions', label: 'الصلاحيات التفصيلية', type: 'permissions', required: true, options: permissionOptions, wide: true },
          { name: 'active', label: 'الدور نشط ويسمح لمستخدميه بالدخول', type: 'checkbox', wide: true },
        ],
      },
      banner: {
        title: editor.item?.id ? 'تعديل البانر' : 'إضافة بانر',
        fields: [
          { name: 'title', label: 'العنوان', required: true },
          { name: 'eyebrow', label: 'النص الصغير' },
          { name: 'subtitle', label: 'الوصف', type: 'textarea', wide: true },
          { name: 'cta', label: 'نص الزر' },
          { name: 'targetUrl', label: 'رابط الزر' },
          { name: 'image', label: 'صورة البانر', upload: 'single', wide: true },
          { name: 'position', label: 'ترتيب العرض', type: 'number' },
          { name: 'startsAt', label: 'بداية النشر (اختياري)', type: 'datetime-local' },
          { name: 'endsAt', label: 'نهاية النشر (اختياري)', type: 'datetime-local' },
          { name: 'background', label: 'لون الخلفية', type: 'color' },
          { name: 'foreground', label: 'لون النص', type: 'color' },
          { name: 'active', label: 'البانر منشور', type: 'checkbox', wide: true },
        ],
      },
      coupon: {
        title: editor.item?.id ? 'تعديل الكوبون' : 'إضافة كوبون',
        fields: [
          { name: 'code', label: 'رمز الكوبون', required: true },
          { name: 'type', label: 'نوع الخصم', type: 'select', required: true, options: [
            { value: 'percent', label: 'نسبة مئوية' },
            { value: 'fixed', label: 'مبلغ ثابت' },
            { value: 'free_shipping', label: 'شحن مجاني' },
          ] },
          { name: 'value', label: 'قيمة الخصم', type: 'number', required: true },
          { name: 'minimum', label: 'الحد الأدنى للطلب', type: 'number' },
          { name: 'maximumDiscount', label: 'أقصى خصم', type: 'number' },
          { name: 'limit', label: 'حد الاستخدام', type: 'number' },
          { name: 'startsAt', label: 'بداية الصلاحية', type: 'datetime-local' },
          { name: 'endsAt', label: 'نهاية الصلاحية', type: 'datetime-local' },
          { name: 'active', label: 'الكوبون نشط', type: 'checkbox', wide: true },
        ],
      },
      payment: {
        title: editor.item?.id ? 'تعديل وسيلة الدفع' : 'إضافة وسيلة دفع',
        fields: [
          { name: 'name', label: 'الاسم', required: true },
          { name: 'shortName', label: 'الاسم المختصر', required: true },
          { name: 'mode', label: 'طريقة المعالجة', type: 'select', required: true, options: [
            { value: 'manual', label: 'تحويل يدوي' },
            { value: 'cash_on_delivery', label: 'دفع عند الاستلام' },
            { value: 'api', label: 'ربط آلي API' },
          ] },
          { name: 'icon', label: 'اسم الأيقونة' },
          { name: 'color', label: 'اللون', type: 'color' },
          { name: 'image', label: 'شعار أو صورة وسيلة الدفع', upload: 'single', wide: true },
          { name: 'instructions', label: 'تعليمات الدفع', type: 'textarea', wide: true },
          { name: 'enabled', label: 'وسيلة الدفع مفعلة', type: 'checkbox', wide: true },
        ],
      },
      shipping: {
        title: editor.item?.id ? 'تعديل منطقة الشحن' : 'إضافة منطقة شحن',
        fields: [
          { name: 'governorate', label: 'المحافظة', required: true },
          { name: 'fee', label: 'رسوم التوصيل', type: 'number', required: true },
          { name: 'eta', label: 'مدة التوصيل', required: true, placeholder: 'مثال: 1-2 يوم' },
          { name: 'image', label: 'صورة المحافظة', upload: 'single', wide: true },
          { name: 'districtsText', label: 'المناطق (افصل بينها بفاصلة)', type: 'textarea', wide: true },
          { name: 'active', label: 'المنطقة متاحة', type: 'checkbox', wide: true },
        ],
      },
      page: {
        title: editor.item?.id ? 'تعديل الصفحة' : 'إضافة صفحة',
        fields: [
          { name: 'title', label: 'عنوان الصفحة', required: true },
          { name: 'slug', label: 'الرابط المختصر', required: true, placeholder: 'about' },
          { name: 'titleEn', label: 'العنوان بالإنجليزية', wide: true },
          { name: 'content', label: 'محتوى الصفحة بالعربية', type: 'textarea', required: true, wide: true },
          { name: 'contentEn', label: 'المحتوى بالإنجليزية', type: 'textarea', wide: true },
          { name: 'published', label: 'الصفحة منشورة', type: 'checkbox', wide: true },
        ],
      },
    };
    return definitions[editor.kind];
  }, [editor, workspace.categories, workspace.inventory.stock, workspace.roles, workspace.permissionCatalog]);

  if (!authenticated) return <LoginScreen logoUrl={logoUrl} onLogin={() => { setCurrentRole(managementApi.role() || 'support'); setAuthenticated(true); }} />;

  function downloadSalesReport() {
    if (!salesReport) return;
    const rows = salesReport.orders.map((order) => [
      order.id, order.createdAt, order.customerName, order.customerPhone, order.city,
      orderStatusLabels[order.status] || order.status, order.itemCount, order.paymentMethod, order.total,
    ]);
    const escapeCell = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
    const csv = [['رقم الطلب', 'التاريخ', 'العميل', 'الهاتف', 'المحافظة', 'الحالة', 'عدد القطع', 'وسيلة الدفع', 'الإجمالي'], ...rows]
      .map((row) => row.map(escapeCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `kingmall-sales-${salesReport.range.from}-${salesReport.range.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function reportsPage() {
    const today = yemeniDateInput();
    const sevenDays = new Date(`${today}T00:00:00Z`);
    sevenDays.setUTCDate(sevenDays.getUTCDate() - 6);
    const report = salesReport;
    const maxSales = Math.max(...(report?.byDay.map((item) => item.sales) ?? []), 1);
    return <>
      <section className="panel report-controls">
        <SectionHeader title="فترة التقرير" note="تُحسب الأيام وفق توقيت اليمن (Asia/Aden)" actions={report && <button className="button secondary" onClick={downloadSalesReport}><Download size={16} /> تنزيل CSV</button>} />
        <form className="report-filter-grid" onSubmit={(event) => { event.preventDefault(); void loadSalesReport(); }}>
          <label><span>من تاريخ</span><input type="date" required max={reportRange.to} value={reportRange.from} onChange={(event) => setReportRange((current) => ({ ...current, from: event.target.value }))} /></label>
          <label><span>إلى تاريخ</span><input type="date" required min={reportRange.from} max={today} value={reportRange.to} onChange={(event) => setReportRange((current) => ({ ...current, to: event.target.value }))} /></label>
          <button className="button primary" disabled={reportLoading}>{reportLoading ? <LoaderCircle className="spin" size={17} /> : <BarChart3 size={17} />} عرض التقرير</button>
        </form>
        <div className="report-shortcuts">
          <button onClick={() => openSalesReport(today, today)}>اليوم</button>
          <button onClick={() => openSalesReport(sevenDays.toISOString().slice(0, 10), today)}>آخر 7 أيام</button>
          <button onClick={() => openSalesReport(`${today.slice(0, 8)}01`, today)}>هذا الشهر</button>
        </div>
      </section>
      {reportLoading && !report ? <div className="page-loading"><LoaderCircle className="spin" size={30} /><strong>جارٍ إعداد التقرير...</strong></div> : report && <>
        <div className="metric-grid report-metrics">
          <article className="metric-card gold"><div><span>إجمالي المبيعات</span><strong>{formatYER(report.summary.sales)}</strong></div><i><TrendingUp size={23} /></i></article>
          <article className="metric-card blue"><div><span>الطلبات غير الملغاة</span><strong>{report.summary.orders.toLocaleString('ar-YE')}</strong></div><i><ClipboardList size={23} /></i></article>
          <article className="metric-card violet"><div><span>العملاء الجدد</span><strong>{report.summary.newCustomers.toLocaleString('ar-YE')}</strong></div><i><Users size={23} /></i></article>
          <article className="metric-card green"><div><span>القطع المباعة</span><strong>{report.summary.items.toLocaleString('ar-YE')}</strong></div><i><Package size={23} /></i></article>
        </div>
        <div className="report-summary-strip">
          <span>متوسط الطلب <strong>{formatYER(report.summary.averageOrder)}</strong></span>
          <span>طلبات ملغاة <strong>{report.summary.cancelledOrders.toLocaleString('ar-YE')}</strong></span>
          {report.statusBreakdown.map((item) => <span key={item.status}>{orderStatusLabels[item.status] || item.status} <strong>{item.count.toLocaleString('ar-YE')}</strong></span>)}
        </div>
        <section className="panel">
          <SectionHeader title="المبيعات حسب اليوم" note={`${report.range.from} — ${report.range.to}`} />
          {report.byDay.length ? <div className="bar-chart report-chart">{report.byDay.map((item) => <div className="bar-item" key={item.date}>
            <div className="bar-value">{item.sales ? formatYER(item.sales) : ''}</div>
            <div className="bar-track"><div style={{ height: Math.max(4, (item.sales / maxSales) * 100) + '%' }} /></div>
            <span>{new Intl.DateTimeFormat('ar-YE', { month: 'short', day: 'numeric' }).format(new Date(`${item.date}T12:00:00Z`))}</span>
          </div>)}</div> : <EmptyState title="لا توجد بيانات" text="لم تُسجل مبيعات في الفترة المختارة." />}
        </section>
        <section className="panel">
          <SectionHeader title="طلبات الفترة" note={`${report.orderRows.toLocaleString('ar-YE')} طلب${report.truncated ? ' — يعرض آخر 2000 طلب، والإجماليات أعلاه كاملة' : ''}`} />
          {report.orders.length ? <div className="table-wrap"><table><thead><tr><th>الطلب</th><th>التاريخ</th><th>العميل</th><th>الحالة</th><th>القطع</th><th>وسيلة الدفع</th><th>الإجمالي</th></tr></thead><tbody>{report.orders.map((order) => <tr className="clickable-row" key={order.id} onClick={() => setSelectedOrder(order)}><td><span className="code">{order.id}</span></td><td>{formatDate(order.createdAt)}</td><td><strong>{order.customerName}</strong><small dir="ltr">{order.customerPhone}</small></td><td><span className={`state-pill ${order.status}`}>{orderStatusLabels[order.status] || order.status}</span></td><td>{Number(order.itemCount).toLocaleString('ar-YE')}</td><td>{order.paymentMethod || '—'}</td><td><strong>{formatYER(order.total)}</strong></td></tr>)}</tbody></table></div> : <EmptyState title="لا توجد طلبات" text="لا توجد طلبات في الفترة المختارة." />}
        </section>
      </>}
    </>;
  }

  function dashboardPage() {
    const stockItems = workspace.inventory.stock.filter((item) => item.active !== false && item.productActive !== false);
    const outOfStock = stockItems.filter((item) => Number(item.stock) <= 0);
    const lowStock = stockItems.filter((item) => Number(item.stock) > 0 && Number(item.stock) < 5);
    const topProducts = workspace.analytics.topProducts.filter((item) => Number(item.soldCount) > 0).slice(0, 6);
    const cards = [
      { label: 'مبيعات اليوم', value: formatYER(workspace.summary.todaySales), icon: BarChart3, tone: 'gold', onClick: can('analytics.read') ? () => openSalesReport(yemeniDateInput(), yemeniDateInput()) : undefined },
      { label: 'إجمالي الطلبات', value: workspace.summary.orders.toLocaleString('ar-YE'), icon: ClipboardList, tone: 'blue' },
      { label: 'المنتجات النشطة', value: workspace.summary.products.toLocaleString('ar-YE'), icon: Package, tone: 'green' },
      { label: 'العملاء المسجلون', value: workspace.summary.customers.toLocaleString('ar-YE'), icon: Users, tone: 'violet' },
    ];
    const maxSales = Math.max(...workspace.analytics.byDay.map((item) => item.sales), 1);
    return (
      <>
        <div className="metric-grid">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <article className={'metric-card ' + card.tone + (card.onClick ? ' clickable' : '')} key={card.label} onClick={card.onClick} role={card.onClick ? 'button' : undefined} tabIndex={card.onClick ? 0 : undefined} onKeyDown={card.onClick ? (event) => { if (event.key === 'Enter' || event.key === ' ') card.onClick?.(); } : undefined}>
                <div><span>{card.label}</span><strong>{card.value}</strong></div>
                <i><Icon size={23} /></i>
              </article>
            );
          })}
        </div>
        <div className="dashboard-grid">
          <section className="panel chart-panel">
            <SectionHeader title="المبيعات خلال 7 أيام" note="الأرقام تأتي مباشرة من الطلبات المسجلة" />
            {workspace.analytics.byDay.length ? (
              <div className="bar-chart">
                {workspace.analytics.byDay.map((item) => (
                  <div className="bar-item" key={item.day}>
                    <div className="bar-value">{item.sales ? formatYER(item.sales) : ''}</div>
                    <div className="bar-track"><div style={{ height: Math.max(4, (item.sales / maxSales) * 100) + '%' }} /></div>
                    <span>{item.day}</span>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="لا توجد مبيعات بعد" text="سيظهر الرسم تلقائيًا عند تسجيل أول طلب." />}
          </section>
          <section className="panel quick-panel">
            <SectionHeader title="إجراءات سريعة" />
            {can('products.manage') && <button onClick={() => { setPage('products'); openEditor('product'); }}><Plus size={19} /> إضافة منتج</button>}
            {can('inventory.manage') && <button onClick={() => { setPage('inventory'); openEditor('inventory'); }}><Boxes size={19} /> حركة مخزون</button>}
            {can('users.manage') && <button onClick={() => { setPage('users'); openEditor('user'); }}><Users size={19} /> مستخدم جديد</button>}
            {can('banners.manage') && <button onClick={() => { setPage('marketing'); openEditor('banner'); }}><Image size={19} /> بانر جديد</button>}
          </section>
        </div>
        <div className="management-insights">
          <section className="panel stock-alert-panel">
            <SectionHeader title="تنبيهات المخزون" note="تظهر للمدير فقط ولا يراها الزبون" actions={<button className="text-button" onClick={() => setPage('inventory')}>فتح المخزون <ChevronLeft size={16} /></button>} />
            {outOfStock.length || lowStock.length ? <div className="stock-alert-list">
              {outOfStock.slice(0, 5).map((item) => <button key={item.id} className="stock-alert danger" onClick={() => setPage('products')}><AlertTriangle size={18} /><span><strong>{item.productName}</strong><small>{item.sku} · نفد المخزون</small></span><b>0</b></button>)}
              {lowStock.slice(0, Math.max(0, 7 - outOfStock.length)).map((item) => <button key={item.id} className="stock-alert warning" onClick={() => setPage('inventory')}><Archive size={18} /><span><strong>{item.productName}</strong><small>{item.sku} · قريب النفاد</small></span><b>{item.stock}</b></button>)}
            </div> : <div className="insight-success"><ShieldCheck size={22} /><span><strong>المخزون بحالة جيدة</strong><small>لا توجد أصناف منخفضة أو نافدة حاليًا.</small></span></div>}
          </section>
          <section className="panel top-products-panel">
            <SectionHeader title="الأصناف الأكثر طلبًا" note="حسب الطلبات المسجلة" />
            {topProducts.length ? <div className="top-products-list">{topProducts.map((product, index) => <article key={product.id}><i>{index + 1}</i><span><strong>{product.name}</strong><small>{formatYER(product.price)}</small></span><b><TrendingUp size={15} /> {product.soldCount} طلب</b></article>)}</div> : <EmptyState title="لا توجد مبيعات كافية" text="ستظهر المنتجات الأكثر طلبًا تلقائيًا." />}
          </section>
        </div>
        {can('orders.read') && <section className="panel">
          <SectionHeader title="أحدث الطلبات" actions={<button className="text-button" onClick={() => setPage('orders')}>كل الطلبات <ChevronLeft size={16} /></button>} />
          {workspace.orders.length ? renderOrders(workspace.orders.slice(0, 6)) : <EmptyState title="لا توجد طلبات" text="سيظهر أول طلب هنا فور إتمامه من المتجر." />}
        </section>}
      </>
    );
  }

  function renderOrders(orders: AdminRecord[]) {
    return (
      <div className="table-wrap">
        <table>
          <thead><tr><th>رقم الطلب</th><th>العميل</th><th>الموقع</th><th>الإجمالي</th><th>الدفع</th><th>التاريخ</th><th>الحالة</th><th>التفاصيل</th></tr></thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td><strong className="code">#{order.id}</strong></td>
                <td><strong>{order.customerName}</strong><small>{order.customerPhone}</small></td>
                <td>
                  <div className="order-location-cell">
                    <strong>{[order.city, order.district].filter(Boolean).join(' · ') || 'لم يحدد'}</strong>
                    {order.address && <small>{order.address}</small>}
                    {order.deliveryLocation?.mapUrl
                      ? <a href={order.deliveryLocation.mapUrl} target="_blank" rel="noopener noreferrer"><MapPin size={15} /> عرض موقع الزبون على الخريطة <ExternalLink size={12} /></a>
                      : <span className="location-missing"><AlertTriangle size={13} /> لم يرسل الزبون موقع GPS</span>}
                    {Number(order.deliveryLocation?.accuracy) > 0 && <small>دقة الموقع نحو {Math.round(Number(order.deliveryLocation.accuracy))} متر</small>}
                  </div>
                </td>
                <td><strong>{formatYER(order.total)}</strong><small>{order.itemCount} منتج</small></td>
                <td>{order.paymentMethod || '—'}</td>
                <td>{formatDate(order.createdAt)}</td>
                <td>
                  <select className={'status-select ' + order.status} value={order.status} disabled={!canUpdateOrders} title={canUpdateOrders ? 'تحديث حالة الطلب' : 'عرض فقط'} onChange={(event) => void updateOrder(order.id, event.target.value)}>
                    {Object.entries(orderStatusLabels).map(([value, label]) => <option value={value} key={value} disabled={value !== order.status && !(orderTransitions[order.status] ?? []).includes(value)}>{label}</option>)}
                  </select>
                </td>
                <td><button type="button" className="order-view-button" onClick={() => setSelectedOrder(order)}><Eye size={16} /> عرض الطلب</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function productsPage() {
    return (
      <>
        <section className="panel catalog-policy-panel">
          <div className="catalog-policy-copy"><i>{settingsDraft.hideOutOfStockProducts ? <EyeOff size={21} /> : <Eye size={21} />}</i><div><strong>سياسة عرض المنتجات النافدة</strong><p>{settingsDraft.hideOutOfStockProducts ? 'المنتجات التي أصبح مجموع مخزونها صفر مخفية تلقائيًا عن العميل.' : 'المنتجات النافدة تبقى ظاهرة للعميل مع عبارة «نفد المخزون».'}</p><small>هذا القرار يُدار من لوحة التحكم فقط ولا يظهر كخيار في متجر العميل.</small></div></div>
          {can('settings.manage') ? <button type="button" className={'button ' + (settingsDraft.hideOutOfStockProducts ? 'primary' : 'secondary')} disabled={saving} onClick={() => void setCatalogVisibilityPolicy(!settingsDraft.hideOutOfStockProducts)}>{saving ? <LoaderCircle className="spin" size={17} /> : settingsDraft.hideOutOfStockProducts ? <Eye size={17} /> : <EyeOff size={17} />}{settingsDraft.hideOutOfStockProducts ? 'إظهار النافد في المتجر' : 'إخفاء النافد تلقائيًا'}</button> : <span className="state-pill inactive">عرض فقط</span>}
        </section>
        <section className="panel">
          <SectionHeader
            title="قائمة المنتجات"
            note={workspace.products.length + ' منتج مسجل'}
            actions={
              <>
                <div className="search-box"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ابحث باسم المنتج أو SKU" /></div>
                {can('categories.manage') && <button className="button secondary" onClick={() => openEditor('category')}><Plus size={17} /> قسم</button>}
                {can('products.manage') && <button className="button primary" onClick={() => openEditor('product')}><Plus size={17} /> منتج جديد</button>}
              </>
            }
          />
          {visibleProducts.length ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>المنتج</th><th>القسم</th><th>السعر</th><th>الرصيد</th><th>الظهور</th><th>الإجراءات</th></tr></thead>
                <tbody>
                  {visibleProducts.map((product) => {
                    const stock = (product.variants ?? []).reduce((sum: number, variant: AdminRecord) => sum + Number(variant.stock || 0), 0);
                    const category = workspace.categories.find((item) => item.id === product.categoryId)?.name ?? 'غير مصنف';
                    return (
                      <tr key={product.id}>
                        <td>
                          <div className="product-cell">
                            {product.images?.[0] ? <img src={product.images[0]} alt="" /> : <span><Package size={19} /></span>}
                            <div><strong>{product.name}</strong><small>{product.sku || product.brand}</small></div>
                          </div>
                        </td>
                        <td>{category}</td>
                        <td><strong>{formatYER(product.price)}</strong></td>
                        <td><span className={stock < 5 ? 'low-stock' : ''}>{stock.toLocaleString('ar-YE')}</span></td>
                        <td><span className={'state-pill ' + (product.active ? 'active' : 'inactive')}>{product.active ? 'ظاهر' : 'مخفي'}</span></td>
                        <td>
                          {(can('products.manage') || can('products.delete')) && <div className="row-actions">
                            {can('products.manage') && <>
                            <button onClick={() => openEditor('product', product)} title="تعديل"><Edit3 size={17} /></button>
                            <button onClick={() => void setProductVisibility(product, !product.active)} title={product.active ? 'إخفاء من المتجر' : 'إظهار في المتجر'}>{product.active ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                            </>}
                            {can('products.delete') && <button className="danger-action" onClick={() => void deleteProductPermanently(product)} title="حذف نهائي آمن"><Trash2 size={17} /></button>}
                          </div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا توجد منتجات" text="لا توجد منتجات مسجلة حاليًا." action={can('products.manage') && <button className="button primary" onClick={() => openEditor('product')}><Plus size={17} /> إضافة منتج</button>} />}
        </section>
        <section className="panel">
          <SectionHeader title="الأقسام" note="تنظيم واجهة المتجر والبحث" actions={can('categories.manage') && <button className="button secondary" onClick={() => openEditor('category')}><Plus size={17} /> إضافة قسم</button>} />
          {workspace.categories.length ? (
            <div className="category-grid">
              {[...workspace.categories].sort((first, second) => Number(Boolean(first.parentId)) - Number(Boolean(second.parentId)) || String(first.name).localeCompare(String(second.name), 'ar')).map((category) => (
                <article className={!category.active ? 'muted-card' : ''} key={category.id}>
                  <i className={category.image ? 'has-image' : ''} style={{ background: category.color }}>{category.image ? <img src={category.image} alt="" /> : category.icon}</i>
                  <div><strong>{category.name}</strong><span>{category.parentId ? `فرعي من ${workspace.categories.find((item) => item.id === category.parentId)?.name || 'قسم رئيسي'} · ` : ''}{workspace.products.filter((product) => product.categoryId === category.id).length} منتج</span></div>
                  {can('categories.manage') && <button onClick={() => openEditor('category', category)}><Edit3 size={16} /></button>}
                </article>
              ))}
            </div>
          ) : <EmptyState title="لا توجد أقسام" text="أضف قسمًا قبل إضافة المنتجات." />}
        </section>
      </>
    );
  }

  function inventoryPage() {
    return (
      <>
        <div className="metric-grid compact">
          <article className="metric-card green"><div><span>إجمالي القطع</span><strong>{workspace.summary.stock.toLocaleString('ar-YE')}</strong></div><i><Boxes size={23} /></i></article>
          <article className="metric-card gold"><div><span>أصناف منخفضة</span><strong>{workspace.summary.lowStock.toLocaleString('ar-YE')}</strong></div><i><Archive size={23} /></i></article>
          <article className="metric-card red"><div><span>أصناف نفدت</span><strong>{workspace.summary.outOfStock.toLocaleString('ar-YE')}</strong></div><i><AlertTriangle size={23} /></i></article>
          <article className="metric-card blue"><div><span>منتجات مخفية</span><strong>{workspace.summary.hiddenProducts.toLocaleString('ar-YE')}</strong></div><i><EyeOff size={23} /></i></article>
        </div>
        <section className="panel">
          <SectionHeader title="أرصدة الأصناف" actions={can('inventory.manage') && <button className="button primary" onClick={() => openEditor('inventory')}><Plus size={17} /> حركة مخزون</button>} />
          {workspace.inventory.stock.length ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>المنتج</th><th>SKU</th><th>اللون / المقاس</th><th>الرصيد</th><th>الحالة</th></tr></thead>
                <tbody>
                  {workspace.inventory.stock.map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.productName}</strong></td>
                      <td><span className="code">{item.sku}</span></td>
                      <td>{item.color} / {item.size}</td>
                      <td><strong className={item.stock < 5 ? 'low-stock' : ''}>{item.stock}</strong></td>
                      <td><span className={'state-pill ' + (item.stock > 0 ? 'active' : 'inactive')}>{item.stock > 0 ? 'متوفر' : 'نفد'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا توجد أصناف بالمخزون" text="أضف منتجًا أولًا ثم سجّل حركات المخزون." />}
        </section>
        <section className="panel">
          <SectionHeader title="سجل الحركات" note="سجل غير قابل للتلاعب لكل تغيير في الرصيد" />
          {workspace.inventory.movements.length ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>التاريخ</th><th>المنتج</th><th>النوع</th><th>الكمية</th><th>الرصيد بعد الحركة</th><th>الملاحظة</th></tr></thead>
                <tbody>
                  {workspace.inventory.movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>{formatDate(movement.createdAt)}</td>
                      <td><strong>{movement.productName}</strong><small>{movement.sku}</small></td>
                      <td>{movementLabels[movement.type] || movement.type}</td>
                      <td><strong className={movement.quantity > 0 ? 'positive' : 'negative'}>{movement.quantity > 0 ? '+' : ''}{movement.quantity}</strong></td>
                      <td>{movement.balanceAfter}</td>
                      <td>{movement.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا توجد حركات بعد" text="أي توريد أو خصم جديد سيظهر هنا." />}
        </section>
      </>
    );
  }

  function usersPage() {
    return (
      <>
        {(can('users.read') || can('users.manage')) && <section className="panel">
          <SectionHeader title="فريق الإدارة" actions={can('users.manage') && <button className="button primary" onClick={() => openEditor('user')}><Plus size={17} /> مستخدم جديد</button>} />
          {workspace.users.length ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>المستخدم</th><th>التواصل</th><th>الدور</th><th>الفرع</th><th>الحالة</th><th>الإجراءات</th></tr></thead>
                <tbody>
                  {workspace.users.map((user) => (
                    <tr key={user.id}>
                      <td><strong>{user.name}</strong><small>{formatDate(user.createdAt)}</small></td>
                      <td>{user.email || user.phone || '—'}</td>
                      <td>{user.roleName || roleLabels[user.role]}</td>
                      <td>{user.branch || 'كل الفروع'}</td>
                      <td><span className={'state-pill ' + (user.active ? 'active' : 'inactive')}>{user.active ? 'نشط' : 'موقوف'}</span></td>
                      <td>{can('users.manage') ? <div className="row-actions"><button onClick={() => openEditor('user', user)} title="تعديل المستخدم"><Edit3 size={17} /></button>{user.id !== workspace.sessionUserId && <button onClick={() => void remove('users', user.id, user.name)} title="إيقاف المستخدم"><Archive size={17} /></button>}</div> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا يوجد مستخدمون" text="أضف أعضاء فريق الإدارة وحدد صلاحياتهم." />}
        </section>}
        {(can('roles.read') || can('roles.manage')) && <section className="panel">
          <SectionHeader title="الأدوار والصلاحيات" note="الصلاحيات تُطبق في الخادم فور الحفظ" actions={can('roles.manage') && <button className="button primary" onClick={() => openEditor('role')}><Plus size={17} /> دور جديد</button>} />
          <div className="role-grid">
            {workspace.roles.filter((role) => role.id !== 'customer').map((role) => (
              <article key={role.id}>
                <div className="role-card-heading"><ShieldCheck size={21} /><span className={'state-pill ' + (role.active ? 'active' : 'inactive')}>{role.active ? 'نشط' : 'موقوف'}</span></div>
                <strong>{role.name}</strong>
                <span>{role.description}</span>
                <ul>{(role.permissionLabels ?? []).map((permission: string) => <li key={permission}>{permission}</li>)}</ul>
                {can('roles.manage') && role.id !== 'admin' && <div className="row-actions"><button onClick={() => openEditor('role', role)} title="تعديل الدور"><Edit3 size={17} /></button>{!role.isSystem && <button onClick={() => void remove('roles', role.id, role.name)} title="حذف الدور"><Trash2 size={17} /></button>}</div>}
              </article>
            ))}
          </div>
        </section>}
      </>
    );
  }

  function marketingPage() {
    return (
      <>
        <section className="panel">
          <SectionHeader title="البانرات" actions={can('banners.manage') && <button className="button primary" onClick={() => openEditor('banner')}><Plus size={17} /> بانر جديد</button>} />
          {workspace.banners.length ? (
            <div className="banner-grid">
              {workspace.banners.map((banner) => (
                <article style={{ background: banner.background, color: banner.foreground }} key={banner.id}>
                  {banner.image && <img src={banner.image} alt="" />}
                  <div><span>{banner.eyebrow}</span><strong>{banner.title}</strong><p>{banner.subtitle}</p></div>
                  {can('banners.manage') && <div className="banner-actions">
                    <button onClick={() => openEditor('banner', banner)}><Edit3 size={16} /></button>
                    <button onClick={() => void remove('banners', banner.id, banner.title)}><Archive size={16} /></button>
                  </div>}
                  <em>{banner.active ? 'منشور' : 'متوقف'}</em>
                </article>
              ))}
            </div>
          ) : <EmptyState title="لا توجد بانرات" text="أنشئ أول بانر ليظهر في واجهة المتجر." />}
        </section>
        <section className="panel">
          <SectionHeader title="كوبونات الخصم" actions={can('coupons.manage') && <button className="button secondary" onClick={() => openEditor('coupon')}><Plus size={17} /> كوبون جديد</button>} />
          {workspace.coupons.length ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th>الكود</th><th>النوع</th><th>القيمة</th><th>الحد الأدنى</th><th>الاستخدام</th><th>الحالة</th><th>الإجراءات</th></tr></thead>
                <tbody>
                  {workspace.coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td><strong className="code">{coupon.code}</strong></td>
                      <td>{coupon.type === 'percent' ? 'نسبة' : coupon.type === 'fixed' ? 'مبلغ ثابت' : 'شحن مجاني'}</td>
                      <td>{coupon.type === 'percent' ? coupon.value + '%' : formatYER(coupon.value)}</td>
                      <td>{formatYER(coupon.minimum)}</td>
                      <td>{coupon.uses} / {coupon.limit || '∞'}</td>
                      <td><span className={'state-pill ' + (coupon.active ? 'active' : 'inactive')}>{coupon.active ? 'نشط' : 'متوقف'}</span></td>
                      <td>{can('coupons.manage') ? <div className="row-actions"><button onClick={() => openEditor('coupon', coupon)}><Edit3 size={17} /></button><button onClick={() => void remove('coupons', coupon.id, coupon.code)}><Archive size={17} /></button></div> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا توجد كوبونات" text="أنشئ كوبونًا عند الحاجة إلى حملة خصم." />}
        </section>
      </>
    );
  }

  function simpleCardsPage(kind: 'payments' | 'shipping') {
    const isPayment = kind === 'payments';
    const items = isPayment ? workspace.payments : workspace.shipping;
    const canManage = can(isPayment ? 'payments.manage' : 'shipping.manage');
    return (
      <section className="panel">
        <SectionHeader
          title={isPayment ? 'وسائل الدفع المتاحة' : 'مناطق الشحن'}
          actions={canManage && <button className="button primary" onClick={() => openEditor(isPayment ? 'payment' : 'shipping')}><Plus size={17} /> {isPayment ? 'وسيلة دفع' : 'منطقة شحن'}</button>}
        />
        {items.length ? (
          <div className="settings-grid">
            {items.map((item) => (
              <article key={item.id}>
                <i className={item.image ? 'has-image' : ''} style={{ background: item.image ? '#fff' : isPayment ? item.color : '#E8F8F2' }}>{item.image ? <img src={item.image} alt="" /> : isPayment ? <CreditCard size={22} /> : <Truck size={22} />}</i>
                <div>
                  <strong>{isPayment ? item.name : item.governorate}</strong>
                  <span>{isPayment ? (item.instructions || item.mode) : formatYER(item.fee) + ' • ' + item.eta}</span>
                  {!isPayment && item.districts?.length ? <small>{item.districts.join('، ')}</small> : null}
                </div>
                <span className={'state-pill ' + ((isPayment ? item.enabled : item.active) ? 'active' : 'inactive')}>{(isPayment ? item.enabled : item.active) ? 'مفعل' : 'متوقف'}</span>
                {canManage && <div className="row-actions">
                  <button onClick={() => openEditor(isPayment ? 'payment' : 'shipping', item)}><Edit3 size={17} /></button>
                  <button onClick={() => void remove(isPayment ? 'payments' : 'shipping-zones', item.id, isPayment ? item.name : item.governorate)}><Archive size={17} /></button>
                </div>}
              </article>
            ))}
          </div>
        ) : <EmptyState title={isPayment ? 'لا توجد وسائل دفع' : 'لا توجد مناطق شحن'} text="استخدم زر الإضافة لبدء الإعداد." />}
      </section>
    );
  }

  function contentPage() {
    return (
      <section className="panel">
        <SectionHeader title="الصفحات الثابتة" actions={can('pages.manage') && <button className="button primary" onClick={() => openEditor('page')}><Plus size={17} /> صفحة جديدة</button>} />
        {workspace.pages.length ? (
          <div className="page-list">
            {workspace.pages.map((item) => (
              <article key={item.id}>
                <i><FileText size={21} /></i>
                <div><strong>{item.title}</strong><span>/{item.slug}</span><p>{String(item.content || '').slice(0, 120) || 'لا يوجد محتوى'}</p></div>
                <span className={'state-pill ' + (item.published ? 'active' : 'inactive')}>{item.published ? 'منشورة' : 'مسودة'}</span>
                {can('pages.manage') && <div className="row-actions"><button onClick={() => openEditor('page', item)}><Edit3 size={17} /></button><button onClick={() => void remove('pages', item.id, item.title)}><Archive size={17} /></button></div>}
              </article>
            ))}
          </div>
        ) : <EmptyState title="لا توجد صفحات" text="أنشئ صفحة معلومات أو سياسة جديدة." />}
      </section>
    );
  }

  function customersPage() {
    const verified = workspace.customers.filter((customer) => customer.phoneVerified).length;
    const buyers = workspace.customers.filter((customer) => Number(customer.orders) > 0).length;
    const located = workspace.customers.filter((customer) => customer.governorate || customer.street).length;
    return (
      <>
        <div className="metric-grid customer-metrics">
          <article className="metric-card violet"><div><span>الحسابات المسجلة</span><strong>{workspace.customers.length.toLocaleString('ar-YE')}</strong></div><i><Users size={23} /></i></article>
          <article className="metric-card green"><div><span>هواتف موثّقة</span><strong>{verified.toLocaleString('ar-YE')}</strong></div><i><ShieldCheck size={23} /></i></article>
          <article className="metric-card blue"><div><span>عملاء لديهم طلبات</span><strong>{buyers.toLocaleString('ar-YE')}</strong></div><i><ClipboardList size={23} /></i></article>
          <article className="metric-card gold"><div><span>عناوين معروفة</span><strong>{located.toLocaleString('ar-YE')}</strong></div><i><MapPin size={23} /></i></article>
        </div>
        <section className="panel">
          <SectionHeader title="تقرير العملاء المسجلين" note="يعرض الحسابات المسجلة فقط؛ العنوان يأتي من أحدث عنوان تسليم محفوظ" actions={<><div className="search-box"><Search size={17} /><input value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="ابحث بالاسم أو الهاتف أو الموقع" /></div><button className="button secondary" disabled={!visibleCustomers.length} onClick={downloadCustomerReport}><Download size={17} /> تنزيل CSV</button></>} />
          {visibleCustomers.length ? (
            <div className="table-wrap customer-report-table">
              <table>
                <thead><tr><th>العميل</th><th>الهاتف</th><th>التحقق</th><th>الدولة</th><th>المحافظة</th><th>المديرية/المنطقة</th><th>الشارع والعنوان</th><th>الطلبات</th><th>إجمالي المشتريات</th><th>تاريخ التسجيل</th></tr></thead>
                <tbody>
                  {visibleCustomers.map((customer) => (
                    <tr key={customer.id}>
                      <td><strong>{customer.name}</strong><small>{customer.email || 'دون بريد إلكتروني'} · {customer.active ? 'حساب نشط' : 'حساب متوقف'}</small></td>
                      <td dir="ltr">{customer.phone || '—'}</td>
                      <td><span className={'state-pill ' + (customer.phoneVerified ? 'active' : 'inactive')}>{customer.phoneVerified ? 'موثّق' : 'غير موثّق'}</span></td>
                      <td>{customer.country || '—'}</td>
                      <td>{customer.governorate || 'لم يحدد'}</td>
                      <td>{customer.district || 'لم يحدد'}</td>
                      <td><strong>{customer.street || 'لم يحدد'}</strong>{customer.addressDetails && customer.addressDetails !== customer.street ? <small>{customer.addressDetails}</small> : null}</td>
                      <td>{Number(customer.orders).toLocaleString('ar-YE')}</td>
                      <td><strong>{formatYER(Number(customer.total))}</strong></td>
                      <td>{formatDate(customer.joined)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState title="لا توجد نتائج" text={workspace.customers.length ? 'غيّر عبارة البحث لعرض العملاء.' : 'سيظهر العميل هنا بعد إنشاء حساب، حتى لو لم ينشئ طلبًا بعد.'} />}
        </section>
      </>
    );
  }

  async function changeStoreImage(key: 'logoUrl' | 'siteBackgroundUrl' | 'heroBackgroundUrl', file?: File) {
    if (!file) return;
    setLogoUploading(true);
    setError('');
    try {
      const uploaded = await managementApi.uploadImage(file);
      const settings = await managementApi.saveSettings({ ...settingsDraft, [key]: uploaded.url });
      setLogoUrl(settings.logoUrl);
      setSettingsDraft(settings);
      setWorkspace((current) => ({ ...current, settings }));
      notify(key === 'logoUrl' ? 'تم تغيير شعار المتجر ونشره بنجاح.' : 'تم تغيير خلفية المتجر ونشرها بنجاح.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر رفع الصورة.');
    } finally {
      setLogoUploading(false);
    }
  }

  async function saveStoreSettings() {
    setSaving(true);
    setError('');
    try {
      const settings = await managementApi.saveSettings(settingsDraft);
      setSettingsDraft(settings);
      setWorkspace((current) => ({ ...current, settings }));
      notify('تم حفظ روابط التواصل والفروع ونشرها.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر حفظ بيانات التواصل والفروع.');
    } finally {
      setSaving(false);
    }
  }

  async function setCatalogVisibilityPolicy(hideOutOfStockProducts: boolean) {
    setSaving(true);
    setError('');
    try {
      const settings = await managementApi.saveSettings({ ...settingsDraft, hideOutOfStockProducts });
      setSettingsDraft(settings);
      setWorkspace((current) => ({ ...current, settings }));
      notify(hideOutOfStockProducts ? 'تم إخفاء المنتجات النافدة تلقائيًا من متجر العميل.' : 'ستبقى المنتجات النافدة ظاهرة مع توضيح نفاد المخزون.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'تعذر تغيير سياسة عرض المنتجات.');
    } finally {
      setSaving(false);
    }
  }

  function updateBranch(index: number, key: 'name' | 'address' | 'phone' | 'mapUrl', value: string) {
    setSettingsDraft((current) => ({
      ...current,
      branches: current.branches.map((branch, branchIndex) => branchIndex === index ? { ...branch, [key]: value } : branch),
    }));
  }

  function brandingPage() {
    return (
      <section className="panel branding-panel">
        <SectionHeader title="شعار المتجر" note="يظهر الشعار في الموقع ولوحة الإدارة فور رفعه" />
        <div className="branding-editor">
          <div className="branding-preview"><img src={logoUrl} alt="معاينة شعار المتجر" /></div>
          <div className="branding-copy">
            <strong>غيّر الشعار من جهازك</strong>
            <p>استخدم صورة واضحة بخلفية شفافة أو مناسبة. الصيغ المدعومة JPG وPNG وWebP، والحد الأقصى 5 ميجابايت.</p>
            <label className="button primary branding-upload">
              <input type="file" accept="image/jpeg,image/png,image/webp" disabled={logoUploading} onChange={(event) => { void changeStoreImage('logoUrl', event.target.files?.[0]); event.currentTarget.value = ''; }} />
              {logoUploading ? <><LoaderCircle className="spin" size={18} /> جارٍ رفع الشعار...</> : <><Image size={18} /> اختر شعارًا من جهازك</>}
            </label>
            <small>سيتم الحفظ والنشر مباشرة بعد اختيار الصورة.</small>
          </div>
        </div>
        <div className="branding-settings">
          <section>
            <div className="branding-section-heading"><div><strong>الخلفيات والخط</strong><p>غيّر مظهر المتجر من جهازك دون تعديل الكود. تبقى النصوص واضحة بفضل طبقة حماية تلقائية.</p></div></div>
            <div className="appearance-settings">
              <article>
                <div className="appearance-preview">{settingsDraft.siteBackgroundUrl ? <img src={settingsDraft.siteBackgroundUrl} alt="معاينة خلفية صفحات المتجر" /> : <span>خلفية الصفحات الافتراضية</span>}</div>
                <strong>خلفية صفحات المتجر</strong>
                <label className="button secondary branding-upload"><input type="file" accept="image/jpeg,image/png,image/webp" disabled={logoUploading} onChange={(event) => { void changeStoreImage('siteBackgroundUrl', event.target.files?.[0]); event.currentTarget.value = ''; }} /><Image size={17} /> رفع خلفية</label>
                {settingsDraft.siteBackgroundUrl && <button className="text-button" onClick={() => setSettingsDraft((current) => ({ ...current, siteBackgroundUrl: '' }))}>إزالة الخلفية</button>}
              </article>
              <article>
                <div className="appearance-preview hero-preview">{settingsDraft.heroBackgroundUrl ? <img src={settingsDraft.heroBackgroundUrl} alt="معاينة خلفية الواجهة الرئيسية" /> : <span>صورة الواجهة الافتراضية</span>}</div>
                <strong>خلفية الواجهة الرئيسية</strong>
                <label className="button secondary branding-upload"><input type="file" accept="image/jpeg,image/png,image/webp" disabled={logoUploading} onChange={(event) => { void changeStoreImage('heroBackgroundUrl', event.target.files?.[0]); event.currentTarget.value = ''; }} /><Image size={17} /> رفع خلفية</label>
                {settingsDraft.heroBackgroundUrl && <button className="text-button" onClick={() => setSettingsDraft((current) => ({ ...current, heroBackgroundUrl: '' }))}>إزالة الخلفية</button>}
              </article>
              <label className="font-picker"><span>خط المتجر</span><select value={settingsDraft.fontFamily} onChange={(event) => setSettingsDraft((current) => ({ ...current, fontFamily: event.target.value as SiteSettings['fontFamily'] }))}><option value="tajawal">Tajawal — واضح وعصري</option><option value="cairo">Cairo — تجاري حديث</option><option value="noto-kufi">Noto Kufi — هندسي أنيق</option><option value="system">خط الجهاز — أسرع تحميلًا</option></select><small>يُطبّق على واجهة الزبون بعد الضغط على حفظ.</small></label>
            </div>
          </section>
          <section>
            <div className="branding-section-heading"><div><strong>روابط التواصل الاجتماعي</strong><p>تظهر هذه الروابط للزبائن في تذييل المتجر.</p></div></div>
            <div className="branding-fields">
              {([
                ['whatsapp', 'رابط واتساب'], ['facebook', 'رابط فيسبوك'], ['instagram', 'رابط إنستجرام'],
                ['threads', 'رابط ثريدز'], ['telegram', 'رابط تيليجرام'],
              ] as Array<[keyof Pick<SiteSettings, 'whatsapp' | 'facebook' | 'instagram' | 'threads' | 'telegram'>, string]>).map(([key, label]) => (
                <label key={key}><span>{label}</span><input type="url" dir="ltr" value={settingsDraft[key]} onChange={(event) => setSettingsDraft((current) => ({ ...current, [key]: event.target.value }))} placeholder="https://" /></label>
              ))}
            </div>
          </section>
          <section>
            <div className="branding-section-heading"><div><strong>فروع كنج مول</strong><p>أضف الاسم والعنوان الصحيحين، ويمكنك إضافة الهاتف ورابط خرائط Google.</p></div><button type="button" className="button secondary" onClick={() => setSettingsDraft((current) => ({ ...current, branches: [...current.branches, { name: '', address: '', phone: '', mapUrl: '' }] }))}><Plus size={16} /> إضافة فرع</button></div>
            {settingsDraft.branches.length ? <div className="branch-editor-list">{settingsDraft.branches.map((branch, index) => (
              <article key={index}>
                <div className="branch-number">{index + 1}</div>
                <label><span>اسم الفرع *</span><input value={branch.name} onChange={(event) => updateBranch(index, 'name', event.target.value)} placeholder="مثال: فرع صنعاء" /></label>
                <label><span>العنوان *</span><input value={branch.address} onChange={(event) => updateBranch(index, 'address', event.target.value)} placeholder="المحافظة، المنطقة، الشارع" /></label>
                <label><span>رقم الهاتف</span><input dir="ltr" value={branch.phone} onChange={(event) => updateBranch(index, 'phone', event.target.value)} /></label>
                <label><span>رابط الخريطة</span><input type="url" dir="ltr" value={branch.mapUrl} onChange={(event) => updateBranch(index, 'mapUrl', event.target.value)} placeholder="https://maps.google.com/..." /></label>
                <button type="button" className="branch-remove" onClick={() => setSettingsDraft((current) => ({ ...current, branches: current.branches.filter((_, branchIndex) => branchIndex !== index) }))} aria-label={`حذف الفرع ${index + 1}`}><Trash2 size={17} /></button>
              </article>
            ))}</div> : <div className="branding-empty">لا توجد فروع مضافة. اضغط «إضافة فرع» وأدخل بيانات الفرع الحقيقية.</div>}
          </section>
          <button type="button" className="button primary branding-save" disabled={saving || logoUploading} onClick={() => void saveStoreSettings()}>{saving ? <LoaderCircle className="spin" size={18} /> : <ShieldCheck size={18} />}{saving ? 'جارٍ الحفظ...' : 'حفظ الهوية والخلفيات والروابط'}</button>
        </div>
      </section>
    );
  }

  function renderPage() {
    if (page === 'dashboard') return dashboardPage();
    if (page === 'reports') return reportsPage();
    if (page === 'products') return productsPage();
    if (page === 'orders') return <section className="panel"><SectionHeader title="كل الطلبات" note={workspace.orders.length + ' طلب'} />{workspace.orders.length ? renderOrders(workspace.orders) : <EmptyState title="لا توجد طلبات" text="لا توجد طلبات مسجلة حتى الآن." />}</section>;
    if (page === 'inventory') return inventoryPage();
    if (page === 'customers') return customersPage();
    if (page === 'users') return usersPage();
    if (page === 'marketing') return marketingPage();
    if (page === 'payments') return simpleCardsPage('payments');
    if (page === 'shipping') return simpleCardsPage('shipping');
    if (page === 'branding') return brandingPage();
    return contentPage();
  }

  return (
    <div className="admin-shell" dir="rtl">
      <aside className={'admin-sidebar ' + (menuOpen ? 'open' : '')}>
        <div className="sidebar-brand">
          <img src={logoUrl} alt="شعار كنج مول الرسمي" />
          <div><strong>كنج مول</strong><span>KING MALL ADMIN</span></div>
          <button onClick={() => setMenuOpen(false)}><X size={19} /></button>
        </div>
        <nav>
          {navGroups.map((group) => ({ ...group, items: group.items.filter((item) => allowedPages.includes(item.key)) })).filter((group) => group.items.length).map((group) => (
            <div className="nav-group" key={group.label}>
              <span>{group.label}</span>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button className={page === item.key ? 'active' : ''} key={item.key} onClick={() => { setPage(item.key); setMenuOpen(false); }}>
                    <Icon size={20} />
                    <strong>{item.label}</strong>
                    <ChevronLeft size={16} />
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-user">
          <i>{adminName.slice(0, 1)}</i>
          <div><strong>{adminName}</strong><span>{currentRoleName}</span></div>
          <span className="sidebar-user-actions">
            <button onClick={() => setPasswordDialog(true)} title="تغيير كلمة المرور" aria-label="تغيير كلمة المرور"><LockKeyhole size={18} /></button>
            <button onClick={logout} title="تسجيل الخروج" aria-label="تسجيل الخروج"><LogOut size={18} /></button>
          </span>
        </div>
      </aside>
      {menuOpen && <button className="sidebar-overlay" onClick={() => setMenuOpen(false)} aria-label="إغلاق القائمة" />}
      <main className="admin-main">
        <header className="admin-topbar">
          <button className="menu-toggle" onClick={() => setMenuOpen(true)}><Menu size={21} /></button>
          <div className="topbar-title">
            <h1>{title.title}</h1>
            <span>{title.subtitle}</span>
          </div>
          <div className="topbar-actions">
            <button className="icon-button" onClick={() => void refresh()} title="تحديث البيانات"><RefreshCw className={loading ? 'spin' : ''} size={19} /></button>
            <div className="system-state"><i /> قاعدة البيانات متصلة</div>
          </div>
        </header>
        <div className="admin-content">
          {error && <div className="page-error"><span>{error}</span><button onClick={() => setError('')}><X size={17} /></button></div>}
          {loading ? (
            <div className="page-loading"><LoaderCircle className="spin" size={34} /><strong>جاري تحميل بيانات المتجر...</strong></div>
          ) : renderPage()}
        </div>
      </main>
      {editor && editorDefinition && (
        <EntityModal
          title={editorDefinition.title}
          item={editor.item}
          fields={editorDefinition.fields}
          saving={saving}
          onClose={() => setEditor(null)}
          onSave={saveEditor}
        />
      )}
      {passwordDialog && <ChangePasswordModal onClose={() => setPasswordDialog(false)} onChanged={() => { managementApi.logout(); setPasswordDialog(false); setAuthenticated(false); }} />}
      {selectedOrder && <OrderDetailsModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {toast && <div className="toast"><ShieldCheck size={18} />{toast}</div>}
    </div>
  );
}
